import { useCallback, useEffect, useState } from 'react';
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Spinner,
  Text,
  MessageBar,
  MessageBarBody,
  Button,
  Input,
  Field,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  DialogTrigger,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  listPartners,
  onboardPartner,
  deletePartner,
  type PartnerSummary,
} from '../../api/partnerClient';
import { useAuthArgs } from '../../hooks/useAuthArgs';

const useStyles = makeStyles({
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalL },
  form: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'end', flexWrap: 'wrap' },
});

/**
 * Admin > Partner Management. Lists onboarded partners (from the storage-account appDataStore) and
 * lets an admin onboard a new partner. Editing a partner's agents/routing/widget is done from the
 * partner detail (link) — this page is the registry overview + onboarding entry point.
 */
export function PartnerManagementPage() {
  const styles = useStyles();
  const [partners, setPartners] = useState<PartnerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [newId, setNewId] = useState('');
  const [newName, setNewName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#107C10');
  const [busy, setBusy] = useState(false);

  const getAuthArgs = useAuthArgs();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to manage partners.');
        return;
      }
      setPartners(await listPartners(auth));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuthArgs]);

  useEffect(() => {
    void load();
  }, [load]);

  const onboard = useCallback(async () => {
    if (!newId.trim()) {
      setError('Enter a partner id.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to onboard a partner.');
        return;
      }
      const partner = await onboardPartner(auth, {
        partnerId: newId.trim(),
        displayName: newName.trim() || undefined,
        brandingTokens: { primaryColor, name: newName.trim() || newId.trim() },
      });
      setNotice(`Onboarded partner "${partner.partnerId}".`);
      setNewId('');
      setNewName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [newId, newName, primaryColor, load, getAuthArgs]);

  const remove = useCallback(async (partnerId: string) => {
    setBusy(true);
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to delete a partner.');
        return;
      }
      await deletePartner(auth, partnerId);
      setNotice(`Deleted partner "${partnerId}".`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [load, getAuthArgs]);

  return (
    <section>
      <div className={styles.header}>
        <Text as="h1" size={600} weight="semibold">Partner Management</Text>
        <Button appearance="secondary" onClick={() => void load()}>Refresh</Button>
      </div>

      {error && <MessageBar intent="error" style={{ marginBottom: 16 }}><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {notice && <MessageBar intent="success" style={{ marginBottom: 16 }}><MessageBarBody>{notice}</MessageBarBody></MessageBar>}

      <div className={styles.form} style={{ marginBottom: 24 }}>
        <Field label="Partner id"><Input value={newId} placeholder="xbox" onChange={(_, d) => setNewId(d.value)} /></Field>
        <Field label="Display name"><Input value={newName} placeholder="Xbox Support" onChange={(_, d) => setNewName(d.value)} /></Field>
        <Field label="Primary color"><input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} style={{ width: 48, height: 32, border: 'none', background: 'none' }} /></Field>
        <Button appearance="primary" disabled={busy || !newId.trim()} onClick={() => void onboard()}>Onboard partner</Button>
      </div>

      {loading ? (
        <Spinner label="Loading partners…" />
      ) : (
        <Table aria-label="Partners">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Partner</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Agents</TableHeaderCell>
              <TableHeaderCell>Widget</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {partners.length === 0 ? (
              <TableRow><TableCell colSpan={5}><Text italic>No partners onboarded yet.</Text></TableCell></TableRow>
            ) : (
              partners.map((p) => (
                <TableRow key={p.partnerId}>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <Text weight="semibold">{p.displayName ?? p.partnerId}</Text>
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{p.partnerId}</Text>
                    </div>
                  </TableCell>
                  <TableCell><Badge appearance="tint" color={statusColor(p.status)}>{p.status}</Badge></TableCell>
                  <TableCell>{p.agentCount}</TableCell>
                  <TableCell>{p.widgetMode ?? '—'}</TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="small" appearance="secondary" onClick={() => { window.location.hash = `#/admin/widgets?partner=${p.partnerId}`; }}>Widget</Button>
                      <Dialog>
                        <DialogTrigger disableButtonEnhancement>
                          <Button size="small" appearance="subtle">Delete</Button>
                        </DialogTrigger>
                        <DialogSurface>
                          <DialogBody>
                            <DialogTitle>Delete partner "{p.partnerId}"?</DialogTitle>
                            <DialogContent>This removes the partner and all of its assets (config + widget bundles). This cannot be undone.</DialogContent>
                            <DialogActions>
                              <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
                              <DialogTrigger disableButtonEnhancement><Button appearance="primary" onClick={() => void remove(p.partnerId)}>Delete</Button></DialogTrigger>
                            </DialogActions>
                          </DialogBody>
                        </DialogSurface>
                      </Dialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

function statusColor(status: string): 'success' | 'warning' | 'danger' | 'brand' {
  switch (status) {
    case 'active': return 'success';
    case 'onboarding': return 'warning';
    case 'suspended':
    case 'offboarded': return 'danger';
    default: return 'brand';
  }
}
